<template>
  <component
    :is="tag"
    :type="tag === 'button' ? type : undefined"
    :to="to"
    :class="[
      'ui-button',
      variantClass,
      { 'ui-button--block': block, 'ui-button--loading': loading },
    ]"
    :disabled="(disabled || loading) && tag === 'button'"
    @click="onClick"
  >
    <span v-if="loading" class="ui-button__spinner" aria-hidden="true" />
    <span class="ui-button__content" :class="{ 'ui-button__content--hidden': loading }">
      <slot />
    </span>
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink } from 'vue-router'

const props = withDefaults(
  defineProps<{
    variant?: 'primary' | 'secondary' | 'ghost'
    type?: 'button' | 'submit' | 'reset'
    disabled?: boolean
    block?: boolean
    loading?: boolean
    /** Маршрут - рендерим как RouterLink */
    to?: string | object
  }>(),
  { variant: 'primary', type: 'button', disabled: false, block: false, loading: false },
)

const emit = defineEmits<{
  click: [event: MouseEvent]
}>()

const tag = computed(() => (props.to ? RouterLink : 'button'))

const variantClass = computed(() => {
  if (props.variant === 'secondary') return 'ui-button--secondary'
  if (props.variant === 'ghost') return 'ui-button--ghost'
  return 'ui-button--primary'
})

function onClick(event: MouseEvent) {
  if (props.disabled || props.loading) {
    event.preventDefault()
    event.stopPropagation()
    return
  }
  emit('click', event)
}
</script>

<style scoped>
.ui-button {
  position: relative;
  padding: 8px 14px;
  min-height: 44px;
  min-width: 44px;
  border-radius: 6px;
  border: 1px solid var(--color-accent);
  cursor: pointer;
  font: inherit;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition:
    background-color 0.15s,
    border-color 0.15s;
}

.ui-button--primary {
  background: var(--color-accent);
  border-color: var(--color-accent);
  color: #fff;
}

.ui-button--primary:hover:not(:disabled) {
  background: var(--color-accent-hover);
  border-color: var(--color-accent-hover);
}

.ui-button--secondary {
  background: #fff;
  color: var(--color-accent);
}

.ui-button--secondary:hover:not(:disabled) {
  background: #eff6ff;
  border-color: var(--color-accent-hover);
  color: var(--color-accent-hover);
}

.ui-button--ghost {
  background: transparent;
  border-color: transparent;
  color: var(--color-accent);
}

.ui-button--ghost:hover {
  background: #eff6ff;
}

.ui-button--block {
  width: 100%;
}

.ui-button:disabled {
  opacity: 0.6;
}

.ui-button--secondary:disabled,
.ui-button--ghost:disabled {
  border-color: #cbd5e1;
  color: #94a3b8;
  background: #fff;
}

.ui-button__spinner {
  position: absolute;
  inset: 0;
  margin: auto;
  width: 20px;
  height: 20px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: ui-button-spin 0.7s linear infinite;
  opacity: 0.9;
}

.ui-button__content--hidden {
  visibility: hidden;
}

@keyframes ui-button-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
